import React, { useCallback, useContext, useMemo } from "react";
import { SettingsSection } from "components/Settings/SettingsSection";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { useFormik } from "formik";
import * as yup from "yup";

import { useMutation } from "@tanstack/react-query";
import { API_URL_INTEGRATION_USER_SETTINGS } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { useIntegrationUserSettingsQuery } from "hooks/queries/useIntegrationUserSettingsQuery";
import {
  IntegrationUserSettingsResponse,
  UpdateIntegrationUserSettings
} from "interfaces/integration-user-settings";
import { UsersContext } from "contexts/UsersContext";
import { UserDropdown } from "components/UserDropdown";

const validationSchema = yup.object({
  anonymous_user: yup
    .number()
    .label("User")
    .required("User is required")
});

export const IntegrationUserSettingsSection = (): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const { loading: usersLoading, error: usersError, users } = useContext(
    UsersContext
  );

  const {
    isLoading: fetchLoading,
    error: fetchError,
    data: fetchData
  } = useIntegrationUserSettingsQuery();

  const patchIntegrationUserSettings = useMutation<
    IntegrationUserSettingsResponse,
    unknown,
    UpdateIntegrationUserSettings
  >(["edit-integration-user-settings"], {
    mutationFn: (newSettings: UpdateIntegrationUserSettings) =>
      axios
        .patch(`${API_URL_INTEGRATION_USER_SETTINGS}`, newSettings)
        .then(response => response.data)
  });

  const {
    isLoading: patchLoading,
    error: patchError
  } = patchIntegrationUserSettings;

  const handleSubmit = useCallback(
    async (
      isValid: boolean,
      values: { anonymous_user: number | null },
      resetForm: any
    ): Promise<void> => {
      if (!isValid || values.anonymous_user === null) return;

      try {
        // Perform the mutation and receive the updated data
        const updatedData = await patchIntegrationUserSettings.mutateAsync({
          anonymous_user: values.anonymous_user
        });

        // Reset the form with the updated data as the new initial values
        resetForm({
          values: {
            anonymous_user: updatedData.anonymous_user
          }
        });

        addSnackbarToQueue({
          message: "Integration user successfully updated",
          severity: "success"
        });
      } catch (err) {
        addSnackbarToQueue({
          message: "Error updating Integration User Settings",
          severity: "error"
        });
      }
    },
    [addSnackbarToQueue, patchIntegrationUserSettings]
  );

  const loading = fetchLoading || patchLoading || usersLoading;
  const error = (fetchError || patchError) instanceof Error;

  const initialValues = useMemo(() => {
    return { anonymous_user: fetchData?.anonymous_user ?? null };
  }, [fetchData]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: values => {
      handleSubmit(formik.isValid, values, formik.resetForm);
    }
  });

  return (
    <SettingsSection title="Integration User Settings">
      <form onSubmit={formik.handleSubmit} noValidate>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-col={1}>Name</TableCell>
                <TableCell data-col={2}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell data-col={1}>Integration User</TableCell>
                <TableCell data-col={2}>
                  <UserDropdown
                    label="User"
                    users={users}
                    error={usersError}
                    helperText={usersError ? "Error getting users" : undefined}
                    disabled={usersLoading}
                    selectedUserId={formik.values.anonymous_user}
                    onChangeSelectedUserId={newValue => {
                      formik.setFieldValue("anonymous_user", newValue);
                    }}
                    avatarTooltip={"Integration User"}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <ButtonWrapper>
          {error && (
            <Typography color="error">
              {"An error occurred while trying to save"}
            </Typography>
          )}
          <Button
            type="submit"
            data-qa="Settings-Button-Save-Integration-User-Settings"
            color="primary"
            variant="contained"
            disabled={
              loading || !formik.dirty || !formik.isValid || formik.isSubmitting
            }
          >
            Save
          </Button>
        </ButtonWrapper>
      </form>
    </SettingsSection>
  );
};

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(1)};
  gap: ${({ theme }) => theme.spacing(1)};
`;
